import { Dialog, Transition, Switch, Tab } from "@headlessui/react";
import React, { Fragment, useState, useContext, useEffect } from "react";
import Close from "../Icons/Close";
import { getUserData } from "../../services/userService";
import { StoreContext } from "../../Context/StoreContext";
import axios from "../../utils/axios";
import UserCard from "../UserCard";
import Button from "../Button";
import { getEventRsvps } from "../../services/eventService";
import Loader from "../Loader";

const UserList = ({
  event,
  rsvpUsers,
  isMoreRsvps,
  handleMoreRsvps,
  isFetchingRsvps,
}) => {
  const { state } = useContext(StoreContext);
  const {
    auth: { user },
    room: { generalRoom },
  } = state;

  function SwitchHighlighted({ rsvp }) {
    const [enabled, setEnabled] = useState(rsvp.isHighLighted);
    const updateRsvpHighlight = async () => {
      axios
        .put(
          `/rsvp/highlight`,
          { event: event._id, isHighLighted: !enabled, userId: rsvp.user._id },
          {
            headers: {
              ...(user?.token
                ? {
                    Authorization: `Bearer ${user?.token}`,
                  }
                : {}),
            },
          },
        )
        .then((res) => {
          if (res.status === 200) {
            rsvp.isHighLighted = !enabled;
            setEnabled(!enabled);
          } else {
            console.log(res);
          }
        });
    };
    return (
      <Switch
        checked={enabled}
        onChange={updateRsvpHighlight}
        className={`${
          enabled ? "bg-blue-600" : "bg-gray-200"
        } relative inline-flex items-center h-6 rounded-full w-11`}
      >
        <span className="sr-only">Enable notifications</span>
        <span
          className={`${
            enabled ? "translate-x-6" : "translate-x-1"
          } inline-block w-4 h-4 transform transition-all bg-white rounded-full`}
        />
      </Switch>
    );
  }

  const shouldRenderSwitch = () => {
    return (
      generalRoom?._id &&
      event?.room &&
      (event.room._id === generalRoom._id ||
        (event.room[0] && event.room[0]._id === generalRoom._id)) &&
      event.owner === user?._id
    );
  };

  return (
    <div className="h-80 overflow-y-auto custom-scrollbar">
      {rsvpUsers?.length > 0 ? (
        <>
          {rsvpUsers.map((rsvp) => (
            <div
              key={rsvp.user._id}
              className="flex justify-between items-center py-2"
            >
              <UserCard user={getUserData(rsvp.user)} size="sm" />
              {shouldRenderSwitch() && (
                <div>
                  <SwitchHighlighted rsvp={rsvp} />
                </div>
              )}
            </div>
          ))}
          <div className="flex justify-center">
            {isMoreRsvps &&
              (isFetchingRsvps ? (
                <Loader />
              ) : (
                <Button onClick={handleMoreRsvps}>Load More</Button>
              ))}
          </div>
        </>
      ) : (
        <p className="text-darkGray text-center mt-20 dark:text-primaryGray">
          No users
        </p>
      )}
    </div>
  );
};

export default function UserListModal({ isOpen, setIsOpen, event }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("confirmed");
  const [searchResults, setSearchResults] = useState([]);
  const [eventRsvps, setEventRsvps] = useState([]);
  const [isFetchingRsvps, setIsFetchingRsvps] = useState(true);
  const [isMoreRsvps, setIsMoreRsvps] = useState(false);
  const [rsvpOffset, setRsvpOffset] = useState(0);

  const fetchEventRsvps = async ({ eventId, status, offset, limit }) => {
    try {
      setIsFetchingRsvps(true);
      const response = await getEventRsvps({ eventId, status, offset, limit });
      setEventRsvps([...eventRsvps, ...response.data]);

      if (response.data.length === limit) {
        setIsMoreRsvps(true);
        setRsvpOffset(offset);
        return;
      }
      setIsMoreRsvps(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetchingRsvps(false);
    }
  };
  useEffect(() => {
    if (event._id && selectedTab && rsvpOffset === 0) {
      fetchEventRsvps({
        eventId: event._id,
        status: selectedTab,
        offset: rsvpOffset,
        limit: 20,
      });
    }
  }, [event._id, selectedTab, rsvpOffset]);

  const handleMoreRsvps = () => {
    fetchEventRsvps({
      eventId: event._id,
      status: selectedTab,
      offset: rsvpOffset + 20,
      limit: 20,
    });
  };

  const filterUsersByUsername = (users, searchTerm) => {
    return users.filter((rsvp) => {
      const user = getUserData(rsvp.user);
      return user.username?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };

  const searchUsersFromList = (searchTerm) => {
    const tabMappings = {
      confirmed: eventRsvps,
      maybe: eventRsvps,
      invited: eventRsvps,
      decline: eventRsvps,
    };

    const searchedUsers = filterUsersByUsername(
      tabMappings[selectedTab],
      searchTerm,
    );

    setSearchResults(searchedUsers);
  };

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    searchUsersFromList(event.target.value);
  };

  useEffect(() => {
    if (searchTerm) {
      searchUsersFromList(searchTerm);
    }
  }, [selectedTab]);

  const handleTabChange = (status) => {
    setRsvpOffset(0);
    setIsMoreRsvps(false);
    setEventRsvps([]);
    setSelectedTab(status);
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 overflow-y-auto"
          onClose={() => setIsOpen(false)}
        >
          <div className="flex md:items-end items-center justify-center min-h-screen pt-4 px-4 pb-0 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-overlay bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block align-bottom bg-white dark:bg-navyGray text-left  shadow-xl transform transition-all  sm:align-middle  w-458px font-ubuntu rounded-2xl ">
                <div className="bg-white dark:bg-navyGray p-4 border-b border-frescoWhite dark:border-matteGray rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-lg text-black dark:text-frescoWhite">
                      All Users
                    </p>
                    <span
                      className="bg-frescoWhite p-9px rounded-full cursor-pointer"
                      onClick={() => {
                        setIsOpen(false);
                      }}
                    >
                      <Close />
                    </span>
                  </div>
                </div>

                <div className="pl-4 pr-6 pt-3 py-2 h-450px">
                  <Tab.Group>
                    <Tab.List className="flex justify-between space-x-1 w-full">
                      <Tab as={Fragment}>
                        {({ selected }) => (
                          <Button
                            variant={selected ? "primary" : "secondary"}
                            onClick={() => {
                              handleTabChange("confirmed");
                            }}
                          >
                            Attending ({event?.rsvp_count})
                          </Button>
                        )}
                      </Tab>
                      <Tab as={Fragment}>
                        {({ selected }) => (
                          <Button
                            variant={selected ? "primary" : "secondary"}
                            onClick={() => {
                              handleTabChange("maybe");
                            }}
                          >
                            Maybe ({event?.maybe_count})
                          </Button>
                        )}
                      </Tab>
                      <Tab as={Fragment}>
                        {({ selected }) => (
                          <Button
                            variant={selected ? "primary" : "secondary"}
                            onClick={() => {
                              handleTabChange("invited");
                            }}
                          >
                            Invited ({event?.invited_count})
                          </Button>
                        )}
                      </Tab>
                      <Tab as={Fragment}>
                        {({ selected }) => (
                          <Button
                            variant={selected ? "primary" : "secondary"}
                            onClick={() => {
                              handleTabChange("declined");
                            }}
                          >
                            Can't Go ({event?.declined_count})
                          </Button>
                        )}
                      </Tab>
                    </Tab.List>
                    <div className="relative pt-3">
                      <input
                        type="text"
                        className="w-full rounded-xl border border-lightGray select-none outline-none placeholder-notifyBg search-input bg-white"
                        placeholder="Search..."
                        onChange={handleChange}
                        value={searchTerm}
                      />
                      <span
                        className="absolute top-6 right-3 bg-notifyBg rounded-full p-[5px] cursor-pointer"
                        onClick={() => {
                          setSearchTerm("");
                        }}
                      >
                        <Close color="#E5E5E5" ratio="8" />
                      </span>
                    </div>
                    {!isFetchingRsvps || isMoreRsvps ? (
                      <Tab.Panels className="mt-4">
                        <Tab.Panel className="h-80 overflow-y-auto custom-scrollbar">
                          <UserList
                            rsvpUsers={
                              searchTerm?.trim().length > 0
                                ? searchResults
                                : eventRsvps
                            }
                            event={event}
                            isMoreRsvps={isMoreRsvps}
                            handleMoreRsvps={handleMoreRsvps}
                            isFetchingRsvps={isFetchingRsvps}
                          />
                        </Tab.Panel>
                        <Tab.Panel className="h-80 overflow-y-auto custom-scrollbar">
                          <UserList
                            rsvpUsers={
                              searchTerm?.trim().length > 0
                                ? searchResults
                                : eventRsvps
                            }
                            event={event}
                            isMoreRsvps={isMoreRsvps}
                            handleMoreRsvps={handleMoreRsvps}
                          />
                        </Tab.Panel>
                        <Tab.Panel className="h-80 overflow-y-auto custom-scrollbar">
                          <UserList
                            rsvpUsers={
                              searchTerm?.trim().length > 0
                                ? searchResults
                                : eventRsvps
                            }
                            event={event}
                            isMoreRsvps={isMoreRsvps}
                            handleMoreRsvps={handleMoreRsvps}
                          />
                        </Tab.Panel>
                        <Tab.Panel className="h-80 overflow-y-auto custom-scrollbar">
                          <UserList
                            rsvpUsers={
                              searchTerm?.trim().length > 0
                                ? searchResults
                                : eventRsvps
                            }
                            event={event}
                            isMoreRsvps={isMoreRsvps}
                            handleMoreRsvps={handleMoreRsvps}
                          />
                        </Tab.Panel>
                      </Tab.Panels>
                    ) : (
                      <div className="py-4 h-80">
                        <Loader />
                      </div>
                    )}
                  </Tab.Group>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
